import {Box, Flex, List, ListItem, Text} from '@chakra-ui/react';
import {OPALITE_BLUE} from '../../constants/designTokens';
import {useUser} from '../../contexts/UserContext';
import usePlannerStyles from '../planner/usePlannerStyles';
import ModalFrame from './ModalFrame';

// Opalite's inbound dial-in number that providers can call directly when they
// would rather place the call from their own phone instead of having Opalite
// dial them.
export const OPALITE_DIAL_IN_NUMBER = '(518) 252-9588';
// Shown when a provider's personal telephonic code hasn't been issued yet
// (legacy accounts predating code backfill). A superadmin can generate one
// from the super admin panel.
export const MISSING_USER_CODE_PLACEHOLDER = 'Not assigned yet';

const DetailRow = ({label, value, valueColor, isPlaceholder, styles}: any) => (
  <Flex
    align="center"
    justify="space-between"
    gap={3}
    py={3}
    borderBottomWidth="1px"
    borderColor={styles.borderColor}
    _last={{borderBottomWidth: 0}}
  >
    <Text color={styles.subtleText} fontSize="sm">
      {label}
    </Text>
    <Text
      fontSize="lg"
      fontWeight="700"
      letterSpacing="0.02em"
      color={isPlaceholder ? styles.subtleText : valueColor || styles.textColor}
      fontStyle={isPlaceholder ? 'italic' : 'normal'}
    >
      {value}
    </Text>
  </Flex>
);

const ManualCallModal = ({isOpen, onClose}: Record<string, any>) => {
  const styles = usePlannerStyles();
  const telephonicCode = useUser()?.userData?.telephonicCode?.trim();

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Text as="span" fontWeight="700">
          Call Opalite manually
        </Text>
      }
      isCentered
      size="md"
      contentProps={{borderRadius: 'xl', mx: 4}}
      bodyProps={{pb: 6}}
    >
      <Text color={styles.subtleText} fontSize="sm" mb={4}>
        Prefer to call from your own phone? Dial Opalite directly and follow
        these steps to bring an interpreter into your call.
      </Text>

      <Box
        borderWidth="1px"
        borderColor={styles.borderColor}
        borderRadius="lg"
        px={4}
        bg={styles.trackBg}
        mb={5}
      >
        <DetailRow
          label="Phone number"
          value={OPALITE_DIAL_IN_NUMBER}
          valueColor={OPALITE_BLUE}
          styles={styles}
        />
        <DetailRow
          label="Your code"
          value={telephonicCode || MISSING_USER_CODE_PLACEHOLDER}
          valueColor={telephonicCode ? OPALITE_BLUE : undefined}
          isPlaceholder={!telephonicCode}
          styles={styles}
        />
      </Box>

      <List as="ol" styleType="decimal" spacing={2} pl={5}>
        <ListItem color={styles.textColor} fontSize="sm">
          Call {OPALITE_DIAL_IN_NUMBER}.
        </ListItem>
        <ListItem color={styles.textColor} fontSize="sm">
          Type or say your code when prompted.
        </ListItem>
        <ListItem color={styles.textColor} fontSize="sm">
          Say the language you need.
        </ListItem>
        <ListItem color={styles.textColor} fontSize="sm">
          Merge the call with your patient.
        </ListItem>
        <ListItem color={styles.textColor} fontSize="sm">
          Say{' '}
          <Text as="strong" fontWeight="700">
            &ldquo;Opalite start&rdquo;
          </Text>{' '}
          to activate Opalite.
        </ListItem>
      </List>
    </ModalFrame>
  );
};

export default ManualCallModal;
